import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { quotesAPI } from '../api';
import { trackQuoteSubmission } from '../utils/analytics';

const InlineQuoteForm = ({ defaultService = '' }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    phone: '',
    email: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    services: defaultService ? [defaultService] : [],
    message: '',
    referralSource: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const serviceOptions = [
    'Window Cleaning',
    'House Washing',
    'Roof Cleaning',
    'Gutter Cleaning',
    'Surface Cleaning (walkway, patio, etc)',
    'Shrink Wrapping (Outdoor Furniture, etc)',
    'Painting / Staining',
    'Other'
  ];

  const referralSources = [
    'Google Search',
    'Social Media',
    'Friend or Family',
    'Saw Our Truck',
    'Repeat Customer',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(null);
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate required fields
    if (!formData.firstName || !formData.phone || !formData.email) {
      setError('Please fill in all required fields.');
      setIsSubmitting(false);
      return;
    }

    if (formData.services.length === 0) {
      setError('Please select at least one service.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Transform data to match backend API
      const quoteData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
        service: formData.services.join(', '),
        propertyType: formData.companyName ? 'Commercial' : 'Residential',
        message: `Address: ${formData.streetAddress}, ${formData.city}, ${formData.state} ${formData.postalCode}\n\nReferral: ${formData.referralSource}\n\n${formData.message}`.trim()
      };

      await quotesAPI.createQuote(quoteData);
      trackQuoteSubmission(formData.services.join(', '), formData.referralSource);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting quote:', err);
      setError(err.response?.data?.detail || 'Failed to submit quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-lg shadow-xl p-6 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">We'll get back to you within 1 business day.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full" data-testid="inline-quote-form">
      <h3 className="text-xl font-bold text-gray-900 mb-4">New Quote Request</h3>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 mb-4">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Row */}
        <div className="grid grid-cols-3 gap-2">
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="First Name*"
            required
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="quote-first-name"
          />
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Last Name*"
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="quote-last-name"
          />
          <input
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            placeholder="Company name"
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="quote-company"
          />
        </div>

        {/* Contact Row */}
        <div className="grid grid-cols-2 gap-2">
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Phone*"
            required
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="quote-phone"
          />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email*"
            required
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="quote-email"
          />
        </div>

        {/* Address */}
        <input
          type="text"
          name="streetAddress"
          value={formData.streetAddress}
          onChange={handleChange}
          placeholder="Street Address*"
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          data-testid="quote-street"
        />

        {/* City, State, Zip */}
        <div className="grid grid-cols-3 gap-2">
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="City*"
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="quote-city"
          />
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleChange}
            placeholder="State*"
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="quote-state"
          />
          <input
            type="text"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            placeholder="Postal Code*"
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="quote-postal"
          />
        </div>

        {/* Service Details */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Service Details</p>
          <div className="grid grid-cols-2 gap-2">
            {serviceOptions.map((service) => (
              <label key={service} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.services.includes(service)}
                  onChange={() => handleServiceToggle(service)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid={`quote-service-${service.toLowerCase().replace(/[^a-z]/g, '-')}`}
                />
                <span className="text-xs text-gray-700">{service}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Message */}
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Please describe any specific requests"
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          data-testid="quote-message"
        />

        {/* Referral Source */}
        <select
          name="referralSource"
          value={formData.referralSource}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500"
          data-testid="quote-referral"
        >
          <option value="">How did you hear about us?*</option>
          {referralSources.map((source) => (
            <option key={source} value={source}>{source}</option>
          ))}
        </select>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="quote-submit-btn"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Submit Quote Request
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default InlineQuoteForm;
